import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, MapPin } from "lucide-react";
import http from "@/lib/api";
import SmartImage from "@/components/SmartImage";

export default function Exhibitions() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(null);

    useEffect(() => {
        http.get("/exhibitions").then(({ data }) => setItems(data));
    }, []);

    useEffect(() => {
        const onEsc = (e) => { if (e.key === "Escape") setOpen(null); };
        if (open) document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [open]);

    const getYt = (url) => url?.match(/v=([^&]+)/)?.[1];

    return (
        <main data-testid="exhibitions-page" className="pt-32 pb-24 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="mb-20 max-w-3xl"
                >
                    <div className="overline mb-4">Actualités</div>
                    <h1 className="serif text-5xl md:text-7xl leading-[0.95] mb-6">
                        Expositions &<br /><span className="italic-serif text-gilt">rétrospectives</span>
                    </h1>
                    <p className="text-bone-200 text-lg leading-relaxed">
                        Patrick Mathurin présente régulièrement son travail lors d'expositions
                        publiques et privées en France.
                    </p>
                </motion.div>

                {/* Timeline */}
                <div className="relative">
                    <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10 md:-translate-x-1/2" />

                    <div className="space-y-16 md:space-y-24">
                        {items.map((ex, i) => {
                            const right = i % 2 === 1;
                            const yt = getYt(ex.video_url);
                            return (
                                <motion.article
                                    key={ex.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ duration: 0.8 }}
                                    data-testid={`exhibition-${i}`}
                                    className={`relative grid md:grid-cols-2 gap-8 md:gap-16 ${right ? "md:[direction:rtl]" : ""}`}
                                >
                                    <div className={`pl-12 md:pl-0 ${right ? "[direction:ltr]" : ""}`}>
                                        <div className="absolute left-4 md:left-1/2 w-2 h-2 bg-gilt rounded-full -translate-x-1/2 mt-3" />
                                        <div className="overline text-gilt mb-3">{ex.month ? `${ex.month} ` : ""}{ex.year}</div>
                                        <h2 className="serif text-3xl md:text-4xl mb-3">{ex.title}</h2>
                                        <div className="flex items-center gap-2 text-bone-300 text-sm mb-5">
                                            <MapPin size={14} strokeWidth={1.2} className="text-gilt" />
                                            <span>{ex.location}</span>
                                        </div>
                                        {ex.description && (
                                            <p className="text-bone-200 leading-relaxed font-light">{ex.description}</p>
                                        )}
                                        {yt && (
                                            <button
                                                onClick={() => setOpen(yt)}
                                                data-testid={`exhibition-video-${i}`}
                                                className="mt-6 inline-flex items-center gap-3 text-sm uppercase tracking-[0.18em] text-gilt hover:text-gilt-light transition-colors"
                                            >
                                                <Play size={14} fill="currentColor" /> Voir le film
                                            </button>
                                        )}
                                    </div>

                                    <div className={`pl-12 md:pl-0 ${right ? "[direction:ltr]" : ""}`}>
                                        {(ex.image_url || ex.video_url) && (
                                            <div className="aspect-[4/3] overflow-hidden bg-ink-800 border border-[var(--border)]">
                                                <SmartImage src={ex.image_url} videoUrl={ex.video_url} alt={ex.title} className="w-full h-full object-cover" fallback={ex.title} />
                                            </div>
                                        )}
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>
                </div>

                {items.length === 0 && (
                    <div className="text-center py-32 text-bone-400">Aucune exposition à afficher pour le moment.</div>
                )}
            </div>

            {/* Video modal */}
            {open && (
                <div
                    className="fixed inset-0 z-[100] backdrop-blur-2xl flex items-center justify-center p-6"
                    style={{ backgroundColor: "var(--bg)" }}
                    onClick={() => setOpen(null)}
                    data-testid="video-modal"
                >
                <button
                    onClick={() => setOpen(null)}
                    className="absolute top-6 right-6 text-bone-50 text-3xl serif hover:text-gilt"
                    aria-label="Fermer"
                >×</button>
                <div className="aspect-video w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
                        <iframe
                            src={`https://www.youtube.com/embed/${open}?autoplay=1`}
                            className="w-full h-full"
                            title="Exposition"
                            allow="autoplay; encrypted-media"
                            allowFullScreen
                        />
                    </div>
                </div>
            )}
        </main>
    );
}
